import React, { useState, useRef, useEffect } from 'react';
import type { Participant } from '../lib/api';

interface PersonPickerProps {
  value: string;
  onChange: (value: string) => void;
  participants: Participant[];
  placeholder?: string;
}

/**
 * Text input with autocomplete dropdown showing known participants.
 * Allows free-text entry as well as picking from the list.
 */
export function PersonPicker({ value, onChange, participants, placeholder }: PersonPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = participants.filter((p) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return p.nickname.toLowerCase().includes(q) || p.shortId.toLowerCase().includes(q);
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    onChange(v);
    setFilter(v);
    if (v.length > 0 && participants.length > 0) {
      setIsOpen(true);
    }
  };

  const handleSelect = (p: Participant) => {
    onChange(p.nickname);
    setFilter('');
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <input
        type="text"
        className="input w-full"
        value={value}
        onChange={handleInputChange}
        onFocus={() => {
          if (participants.length > 0) setIsOpen(true);
        }}
        placeholder={placeholder}
        autoComplete="off"
      />
      {isOpen && filtered.length > 0 && (
        <ul className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.map((p) => (
            <li key={p.deviceId}>
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 flex items-center justify-between gap-2"
                onMouseDown={(e) => {
                  e.preventDefault(); // prevent blur before click registers
                  handleSelect(p);
                }}
              >
                <span className="truncate">{p.nickname}</span>
                <span className="text-xs text-gray-400 flex-shrink-0">({p.shortId})</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
